// Two-step verification (optional; Account → Two-step verification): Supabase Auth TOTP factors, codes from an
// authenticator app. An account with a verified factor reaches the panel only with an aal2 session (the password, then
// the 6-digit code). The server enforces the same rule (supabase/functions/_shared/aal.ts and the RLS policy
// keept_mfa_required); this file only decides which screen to show.

interface SessionLike {
  access_token: string;
  user: { factors?: ReadonlyArray<{ status?: string | null; factor_type?: string | null }> | null };
}

/** The `aal` claim of an access token ("aal1" when missing or unreadable). The server checks the token itself. */
export function aalOfToken(token: string): "aal1" | "aal2" {
  const part = token.split(".")[1];
  if (!part) return "aal1";
  try {
    const b64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const claims = JSON.parse(atob(b64 + "=".repeat((4 - b64.length % 4) % 4)));
    return claims?.aal === "aal2" ? "aal2" : "aal1";
  } catch {
    return "aal1";
  }
}

/** The account uses two-step verification (a verified factor). */
export const usesSecondStep = (s: SessionLike | null | undefined) => !!s?.user.factors?.some((f) => f?.status === "verified");

/** Signed in with the password, but the authenticator code hasn't been entered yet: show the code step, not the panel. */
export const needsSecondStep = (s: SessionLike | null | undefined) => !!s && usesSecondStep(s) && aalOfToken(s.access_token) !== "aal2";

/** "JBSW Y3DP EHPK 3PXP": the setup key in groups of four, easier to type into an app by hand. */
export const groupKey = (secret: string) => secret.replace(/\s+/g, "").replace(/(.{4})(?=.)/g, "$1 ");

/** The 403 the functions answer when the session hasn't passed the code step (http.ts signedIn). */
export const MFA_REQUIRED_CODE = "mfa_required";

/** Where a password change runs while its emailed code holds an aal1 session (App.tsx keeps it on screen instead of the
 *  code step): Forgot password, and Account → Change password (Settings for older links). */
export const isPasswordChangePath = (pathname: string) => /^\/(forgot-password|panel\/(account|settings))(\/|$)/.test(pathname);
